import React, { useEffect, useRef, useState } from 'react';

// Utilities
import API, { socketPurchases } from '../../../../utils/api';
import utils from '../../../../utils/utils';
import gaUtils from '../../../GAWrapper/GAWrapper';
import { waitUserCancelCashoutsPending } from '../../shared/utils/cashouts';
import { getTokenizeCardErrorMessage, tokenizeCard } from './utils';

// Store
import usePaymentStore from '../../shared/store/use-payment-store';

// Hooks
import { useOpenpay } from './use-openpay';
import { useOpenpayCards } from './use-openpay-cards';

// Components
import { CardForm } from '../../shared/components/card-form';
import styles from './card.module.css';
import { CardButton, CardsGrid } from '../../shared/components/card-button';
import Iconify from '../../../Iconify';
import Text from '../../../Text/Text';

// Modals
import { showModalError } from '../../shared/components/modals/modal-error';
import {
  ModalIframe3DSecure,
  showModalIframe3DSecure,
} from '../../shared/components/modals/modal-iframe-3d-secure';
import { showModalSuccessPayment } from '../../shared/components/modals/modal-success';
import { waitVerificationName } from '../../shared/components/modals/modal-verification-name';
import {
  showModalConfirmDeleteCard,
  ModalConfirmDeleteCard,
} from '../../shared/components/modals/modal-confirm-delete';
import iconoAbonar from '../../shared/assets/icono-boton-depositar.png';

// Assets
import openpayLogo from './openpay-logo.png';
import { CardBrandHeader } from '../../shared/components/card-brand-header';
import { PAYMENT_METHOD } from '../../shared/constants';

export const CardOpenpay = ({ setSelectedMethod, ...props }) => {
  const cvvInputRef = useRef(null);
  const { data: openpayCards, refetch } = useOpenpayCards();
  const { isInvalidAmount, amount } = usePaymentStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [cvv, setCvv] = useState('');
  const { deviceSessionId } = useOpenpay();

  useEffect(() => {
    socketPurchases.on('purchase:completed', data => {
      const transaction = {
        amount: data.amount,
        provider: 'OpenPay',
        transactionId: data?.trans_id,
      };

      API.trackPurchaseSuccessFB(transaction);
      gaUtils.trackUserPurchase(transaction);

      showModalIframe3DSecure({
        url: data.status_url,
        title: 'ABONO COMPLETADO',
      });

      showModalSuccessPayment({
        amount: data.amount,
        balance: data.balance,
        trans_id: data.trans_id,
      });

      if (props.dispatch) {
        props.updateUserInfo(props.dispatch);
      } else if (props.updateUserBalance) {
        props.updateUserBalance(data.balance);
      }
    });

    socketPurchases.on('purchase:failed', data => {
      showModalIframe3DSecure({
        url: data.status_url,
        title: 'Error',
      });
    });

    return () => {
      socketPurchases.off('purchase:completed');
      socketPurchases.off('purchase:failed');
    };
  }, []);

  const processPaymentWithNewCard = async cardData => {
    setIsSubmitting(true);

    if (isSubmitting || isInvalidAmount) return;

    try {
      // const isVerified = await waitVerificationName({
      //   title: 'Verificación de nombre',
      // });

      // if (!isVerified) {
      //   alert('Por favor, verifica tu cuenta para continuar.');
      //   return;
      // }

      const userCancell = await waitUserCancelCashoutsPending();

      if (userCancell) {
        setIsSubmitting(false);
        return;
      }

      const card = {
        holder_name: cardData.holderName,
        card_number: cardData.cardNumber,
        expiration_month: cardData.expirationMonth,
        expiration_year: cardData.expirationYear,
        cvv2: cardData.cvv,
      };

      const tokenizeCardResponse = await tokenizeCard(card);

      // Si no se guarda nueva tarjeta
      const paymentDataModel = {
        with3dsecure: true,
        amount: 'other',
        token: tokenizeCardResponse.data.id,
        other_amount: amount,
        card_id: null,
        cvv2: null,
        device_session_id: deviceSessionId,
        card: null,
        name: null,
        last_name: null,
        method: 'credit',
        gateway: 'openpay',
        save_card: cardData?.saveCard,
        extra: {
          url: window.location.href,
          cookies: utils.getCookiesKeyVal(),
        },
      };

      const response = await API.postDoPurchase(paymentDataModel);

      if (!response.data.success) {
        showModalError({ errorMessage: response.data?.error ?? 'Ha ocurrido un error' });

        if (cardData?.saveCard) {
          refetch();
        }
        return;
      }

      if (response.data.show_redirect) {
        showModalIframe3DSecure({
          url: response.data.redirect_url,
          title: '3D Secure',
        });
      }
    } catch (error) {
      console.error('Error al procesar el pago:', error);
      const errorMessage = getTokenizeCardErrorMessage(error?.data?.error_code);
      showModalError({ errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const processPaymentWithSavedCard = async () => {
    setIsSubmitting(true);

    if (isSubmitting || isInvalidAmount) return;

    try {
      // const isVerified = await waitVerificationName({
      //   title: 'Verificación de nombre',
      // });

      // if (!isVerified) {
      //   alert('Por favor, verifica tu cuenta para continuar.');
      //   return;
      // }

      const userCancell = await waitUserCancelCashoutsPending();

      if (userCancell) {
        setIsSubmitting(false);
        return;
      }

      const paymentDataModel = {
        with3dsecure: true,
        amount: 'other',
        token: null,
        other_amount: amount,
        card_id: selectedCard?.id,
        cvv2: cvv,
        device_session_id: deviceSessionId,
        card: null,
        name: null,
        last_name: null,
        method: 'credit',
        gateway: 'openpay',
        extra: {
          url: window.location.href,
          cookies: utils.getCookiesKeyVal(),
        },
      };

      const response = await API.postDoPurchase(paymentDataModel);
      if (!response.data.success) {
        showModalError({ errorMessage: response.data?.error ?? 'Ha ocurrido un error' });
        return;
      }

      if (response.data.show_redirect) {
        showModalIframe3DSecure({
          url: response.data.redirect_url,
          title: '3D Secure',
        });
      }
    } catch (error) {
      console.error('Error al procesar el pago:', error);
      showModalError({
        errorMessage: error?.message ?? 'Error al procesar el pago. Por favor, intenta de nuevo.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCard = async (e, card) => {
    e.stopPropagation();
    e.preventDefault();

    const needDelete = await showModalConfirmDeleteCard({
      card,
    });

    if (needDelete) {
      const response = await API.postOpenPayCardsDelete(card.id);

      if (!response.data.success) {
        const errorMessage = response.data?.error ?? 'Ha ocurrido un error';
        showModalError({ errorMessage });
        return;
      }

      refetch();
      toastSuccess('Tarjeta eliminada exitosamente');
    }

    setSelectedCard(null);
    setCvv('');
  };

  return (
    <div className={styles.container}>
      <ModalIframe3DSecure />
      <ModalConfirmDeleteCard />
      <CardBrandHeader variant="all" />
      <CardsGrid>
        {openpayCards?.map(card => (
          <CardButton
            key={card.id}
            card={card}
            isSelected={selectedCard?.id === card.id}
            onSelectedCard={() => {
              setSelectedCard(card);
              setCvv('');
              cvvInputRef.current?.focus();
            }}
            onDeleteCard={handleDeleteCard}
            selected={selectedCard?.id === card.id}
          />
        ))}
        <CardButton
          card={null}
          isSelected={!selectedCard}
          onSelectedCard={() => {
            setSelectedCard(null);
            setCvv('');
          }}
        >
          <Iconify icon="ic:outline-add-card" style={{ fontSize: 20 }} />
          <Text size={14} style={{ width: '100px' }}>
            Nueva tarjeta
          </Text>
        </CardButton>
      </CardsGrid>
      {!selectedCard && (
        <>
          <CardForm
            onSubmit={processPaymentWithNewCard}
            isSubmitting={isSubmitting}
            disabled={isInvalidAmount}
            onNavigateToOtherCard={() => {
              if (setSelectedMethod) {
                setSelectedMethod(PAYMENT_METHOD.OTHER_CARD);
              }
            }}
          />
        </>
      )}
      {selectedCard && (
        <>
          <div className={styles.cvvContainer}>
            <div className={styles.cvvTitleContainer}>
              <h3 className={styles.cvvTitle}>CÓDIGO DE SEGURIDAD</h3>
              <p className={styles.cvvSubtitle}>
                Ingresa el código del reverso de tu tarjeta con terminacion (
                {selectedCard?.card_number}).
              </p>
            </div>
            <input
              ref={cvvInputRef}
              type="text"
              className={styles.cvvInput}
              placeholder="CVV"
              value={cvv}
              autoFocus
              onChange={e => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                setCvv(value);
              }}
              maxLength={4}
              inputMode="numeric"
            />
          </div>
          <button
            onClick={processPaymentWithSavedCard}
            className="payment-button"
            disabled={isSubmitting || isInvalidAmount || !cvv || cvv.length < 3}
            style={{
              padding: '10px 60px',
              marginTop: '15px',
              marginLeft: 'auto',
              marginBottom: '15px',
              marginRight: 'auto',
              display: 'flex',
              gap: '10px',
            }}
          >
            {isSubmitting ? 'Abonando...' : 'Abonar'}
            <img src={iconoAbonar} alt="Abonar" style={{ width: '20px', height: '20px' }} />
          </button>
        </>
      )}

      <p className={styles.infoText}>
        Los cargos a tu tarjeta de crédito serán realizados a través de{' '}
        <a href="https://www.openpay.mx" target="_blank" rel="noopener noreferrer">
          Openpay
        </a>
        .
      </p>

      <p className={styles.infoText}>
        <a href="https://www.openpay.mx" target="_blank" rel="noopener noreferrer">
          <img src={openpayLogo} alt="Openpay" className={styles.openpayLogo} />
        </a>
      </p>
    </div>
  );
};
